import { useState } from 'react';
import * as XLSX from 'xlsx';
import { Download, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { fetchDetalharAgrupado } from '@/services/SimplesNacional/Segregacao/api/SegregacaoApi';
import { useApuracaoSN } from '@/contexts';
import { useSegregacaoFiltros } from '../../useSegregacaoFiltros';
import type { SegregacaoLinhaReceita } from '@/services/SimplesNacional/Segregacao/api/types';

export function ExportarSegregacaoButton() {
  const [isExporting, setIsExporting] = useState(false);
  const { segregacao } = useApuracaoSN();
  const calcularPayload = useSegregacaoFiltros();
  const { toast } = useToast();

  const handleExportar = async () => {
    if (!segregacao || !calcularPayload) return;

    setIsExporting(true);
    try {
      const agrupadoResp = await fetchDetalharAgrupado({
        Cnpj_Contribuinte: calcularPayload.CnpjContribuinte,
        Ano: calcularPayload.Ano,
        Mes: calcularPayload.Mes,
      });

      const wb = XLSX.utils.book_new();

      // Sheet 1 — Segregação SN (resumo + linhas por categoria)
      const tot = segregacao.TOT?.[0];
      const snData: (string | number)[][] = [];

      snData.push(['TOTAIS GERAL', '', '', '', '', '']);
      snData.push([
        'Total de Saídas',
        'Total Cancelados',
        'Não Gerador de Receita',
        'Faturamento',
        'Não Segregado',
        'Receita Segregada',
      ]);
      snData.push([
        tot?.Total_Saidas ?? 0,
        tot?.Total_Cancelados ?? 0,
        tot?.Total_NaoGeradorReceita ?? 0,
        tot?.Total_Faturamento ?? 0,
        tot?.Total_NaoSegregado ?? 0,
        tot?.Total_Segregado ?? 0,
      ]);
      snData.push([]);

      const addRevenueSection = (
        titulo: string,
        subtitulo: string,
        linhas: SegregacaoLinhaReceita[]
      ) => {
        snData.push([titulo]);
        if (subtitulo) snData.push([subtitulo]);
        snData.push([
          '(*) Receita Bruta',
          '(-) Devoluções',
          '= Receita Líquida',
          'COFINS',
          'CSLL',
          'ICMS',
          'INSS',
          'IRPJ',
          'PIS',
        ]);
        if (!linhas.length) {
          snData.push(['Não há valores a serem lançados na DAS']);
        } else {
          linhas.forEach(l =>
            snData.push([
              l.Receita_Bruta,
              l.Devolução,
              l.Receita_Liquida,
              l.COFINS,
              l.CSLL,
              l.ICMS,
              l.INSS,
              l.IRPJ,
              l.PIS,
            ])
          );
        }
        snData.push([]);
      };

      addRevenueSection(
        'Revenda de mercadoria, exceto para o exterior',
        'Sem substituição Tributária / Tributação Monofásica / Antecipação com encerramento de ciclo',
        segregacao.RIS ?? []
      );
      addRevenueSection(
        'Revenda de mercadoria, exceto para o exterior',
        'Com substituição Tributária / Tributação Monofásica / Antecipação com encerramento de ciclo',
        segregacao.RIC ?? []
      );
      addRevenueSection('Revenda de Mercadoria para o Exterior', '', segregacao.REX ?? []);
      addRevenueSection(
        'Venda de mercadoria industrializada pelo contribuinte, exceto para o exterior',
        'Sem substituição Tributária / Tributação Monofásica / Antecipação com encerramento de ciclo',
        segregacao.IIS ?? []
      );
      addRevenueSection(
        'Venda de mercadoria industrializada pelo contribuinte, exceto para o exterior',
        'Com substituição Tributária / Tributação Monofásica / Antecipação com encerramento de ciclo',
        segregacao.IIC ?? []
      );
      addRevenueSection(
        'Venda de mercadoria industrializada pelo contribuinte para o exterior',
        '',
        segregacao.IEX ?? []
      );

      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(snData), 'Segregação SN');

      // Sheet 2 — Produtos Segregados (SEG)
      const segData: (string | number)[][] = [
        [
          'Produto',
          'Descrição',
          'EAN',
          'NCM',
          'Status',
          'Cenário',
          'CFOP',
          'Saída CST (ICMS)',
          'CST PIS',
          'Valor Total',
        ],
      ];
      (segregacao.SEG ?? []).forEach(s =>
        segData.push([
          s.Produto,
          s.Descricao,
          s.EAN,
          s.NCM,
          s.Status,
          s.cenSegregacaoSNAC,
          s.CFOP,
          s.SaidaCST,
          s.CSTPisSaida,
          s.ValTot,
        ])
      );
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(segData), 'Produtos Segregados');

      // Sheet 3 — Notas Fiscais (CHV — chaves de acesso)
      const chvData: (string | number)[][] = [['Chave de Acesso', 'Status', 'Valor Total']];
      (segregacao.CHV ?? []).forEach(c => chvData.push([c.ChaveAcessoDOC, c.Status, c.ValTot]));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(chvData), 'Notas Fiscais');

      // Sheet 4 — Itens Das Notas Fiscais (ITM)
      const itmData: (string | number)[][] = [
        ['Chave de Acesso', 'Produto', 'CFOP', 'Status', 'Saída CST', 'CST PIS', 'Valor Total'],
      ];
      (segregacao.ITM ?? []).forEach(i =>
        itmData.push([
          i.ChaveAcessoDOC,
          i.Produto,
          i.CFOP,
          i.Status,
          i.SaidaCST,
          i.CSTPisSaida,
          i.ValTot,
        ])
      );
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(itmData), 'Itens Das NF');

      // Sheets 5-8 — Itens Agrupados (DetalharAgrupado)
      const agrupados = agrupadoResp.data ?? [];

      const makeAgrupadoSheet = (items: typeof agrupados): (string | number)[][] => {
        const rows: (string | number)[][] = [
          ['Produto', 'Descrição', 'EAN', 'NCM', 'Valor Total', 'ICMS', 'PIS', 'COFINS'],
        ];
        items.forEach(p =>
          rows.push([p.Produto, p.Descricao, p.EAN, p.NCM, p.ValTot, p.ICMS, p.PIS, p.COFINS])
        );
        return rows;
      };

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(makeAgrupadoSheet(agrupados)),
        'Itens Agrupados'
      );
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(
          makeAgrupadoSheet(agrupados.filter(p => !!p.ICMS && p.ICMS !== '0'))
        ),
        'ICMS - Itens Agrupados'
      );
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(makeAgrupadoSheet(agrupados.filter(p => !!p.PIS && p.PIS !== '0'))),
        'PIS - Itens Agrupados'
      );
      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(
          makeAgrupadoSheet(agrupados.filter(p => !!p.COFINS && p.COFINS !== '0'))
        ),
        'COFINS - Itens Agrupados'
      );

      const fileName = `calculo_segregacao_${calcularPayload.CnpjContribuinte}_${calcularPayload.Ano}_${calcularPayload.Mes}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch {
      toast({
        title: 'Erro ao exportar',
        description: 'Não foi possível gerar o arquivo.',
        variant: 'destructive',
      });
    } finally {
      setIsExporting(false);
    }
  };

  if (!segregacao) return null;

  return (
    <Button
      size="sm"
      variant="outline"
      className="h-7 text-xs gap-1"
      onClick={handleExportar}
      disabled={isExporting}
    >
      {isExporting ? (
        <Loader2 className="h-3 w-3 animate-spin" />
      ) : (
        <Download className="h-3 w-3" />
      )}
      Exportar Cálculo
    </Button>
  );
}
